import _extends from '@babel/runtime/helpers/extends';
import _jsx from '@babel/runtime/helpers/jsx';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import React, { forwardRef, useRef, useContext, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import isWebviewIOSOs from '@andes/common/build/utils/isWebviewIOS';
import usePreventScroll from '@andes/common/build/hooks/usePreventScroll';
import useModal from '@andes/common/build/hooks/useModal';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { useId, getPatchedI18n, AndesContext } from '@andes/context';
import { hideWebviewTopbar, disableWebviewTopbar, showWebviewTopbar, enableWebviewTopbar } from '@andes/common/build/utils/webviewTopbarHandler';
import { FocusScope } from '@react-aria/focus';
import { Portal } from '@andes/common';
import { useDialog } from '@react-aria/dialog';
import { useOverlay } from '@react-aria/overlays';
import { SPACE, ENTER } from '@andes/common/build/constants/keyboardCodes';
import Close20 from '@andes/icons/Close20';
const isLocationURL = url => window.location.href === url;
const addChange = action => {
  if (typeof window.history.pushState === 'function') {
    window.addEventListener('popstate', action);
  }
};
const removeChange = action => {
  if (typeof window.history.pushState === 'function') {
    window.removeEventListener('popstate', action);
  }
};
const redirect = (url, title) => {
  if (!isLocationURL(url)) {
    if (typeof window.history.pushState === 'function') {
      window.history.pushState(undefined, title, url);
    } else {
      window.location = url;
    }
  }
};
const back = url => {
  if (isLocationURL(url)) {
    window.history.back();
  }
};
const _excluded = ["innerRef", "type"];
const withNavigation = Component => {
  class ModalNavigation extends React.Component {
    constructor(props) {
      super(props);
      const {
        isOpen,
        open
      } = this.props;
      this.state = {
        isVisible: open || isOpen || false,
        isClosing: false
      };
      this.handleOpen = this.handleOpen.bind(this);
      this.handleClose = this.handleClose.bind(this);
      this.closeModal = this.closeModal.bind(this);
      this.navigation = this.navigation.bind(this);
      this.hideTopbar = this.hideTopbar.bind(this);
      this.showTopbar = this.showTopbar.bind(this);
    }
    componentDidMount() {
      const {
        url = ''
      } = this.props;
      if (url) addChange(this.navigation);
      this.handleOpen();
    }
    UNSAFE_componentWillReceiveProps(_ref) {
      let {
        open,
        isOpen,
        onOpen
      } = _ref;
      const {
        isVisible
      } = this.state;
      const computedOpen = open || isOpen || false;
      if (isVisible !== computedOpen) {
        if (computedOpen) {
          if (typeof onOpen === 'function') onOpen();
          this.setState({
            isVisible: true
          }, this.handleOpen);
        } else {
          this.handleClose();
        }
      }
    }
    componentWillUnmount() {
      const {
        url = ''
      } = this.props;
      if (url) removeChange(this.navigation);
      if (this.backEventSubscriptionId) this.showTopbar({
        subscriptionIds: this.backEventSubscriptionId
      });
    }
    handleOpen() {
      const {
        isVisible
      } = this.state;
      const {
        url = '',
        title
      } = this.props;
      if (isVisible) {
        this.backEventSubscriptionId = this.hideTopbar({
          onBack: this.handleClose
        });
        if (url) redirect(url, title);
      } else if (url) {
        this.showTopbar({
          subscriptionIds: this.backEventSubscriptionId
        });
        back(url);
      }
    }
    handleClose(event) {
      const {
        onClose
      } = this.props;
      if (event) {
        event.preventDefault();
        if (event.persist) event.persist();
      }
      this.closeModal(() => {
        if (typeof onClose === 'function') onClose(event);
        this.handleOpen();
      });
    }
    closeModal(onClose) {
      this.setState({
        isClosing: true
      });
      this.showTopbar({
        subscriptionIds: this.backEventSubscriptionId
      });
      setTimeout(() => {
        this.setState({
          isVisible: false,
          isClosing: false
        }, onClose);
      }, 180);
    }
    navigation() {
      const {
        url = ''
      } = this.props;
      const {
        isVisible
      } = this.state;
      if (isLocationURL(url)) {
        this.handleOpen();
      } else if (isVisible) {
        this.handleClose();
      }
    }
    hideTopbar(args) {
      const {
        type
      } = this.props;
      return type === 'full' ? hideWebviewTopbar(args) : disableWebviewTopbar(args);
    }
    showTopbar(args) {
      const {
        type
      } = this.props;
      return type === 'full' ? showWebviewTopbar(args) : enableWebviewTopbar(args);
    }
    render() {
      const {
        isVisible,
        isClosing
      } = this.state;
      const _this$props = this.props,
        {
          innerRef,
          type = 'small'
        } = _this$props,
        otherProps = _objectWithoutProperties(_this$props, _excluded);
      return (isVisible || isClosing) && /*#__PURE__*/React.createElement(Component, _extends({
        ref: innerRef,
        handleClose: this.handleClose
      }, this.state, {
        type: type
      }, otherProps));
    }
  }
  return /*#__PURE__*/React.forwardRef((props, ref) => {
    const id = useId(props.id);
    return /*#__PURE__*/React.createElement(ModalNavigation, _extends({}, props, {
      id: id,
      innerRef: ref
    }));
  });
};
var withNavigation$1 = withNavigation;
const namespace$5 = 'andes-modal';
const SECONDARY_HEADER_SCROLL_SHOW = 35;
const SECONDARY_HEADER_SHOW_TITLE_SCROLLS = {
  small: 40,
  large: 48,
  full: 56,
  card: 40
};
const FIXED_ACTIONS_PADDINGS = {
  small: {
    initial: 16,
    final: 32
  },
  large: {
    initial: 16,
    final: 48
  },
  card: {
    initial: 16,
    final: 24
  },
  full: {
    initial: 16,
    final: 24
  }
};
const manageClassList = (method, element, className) => {
  if (!method || !(element !== null && element !== void 0 && element.classList) || !className) return;
  element.classList[method](className);
};
const isContentScrollable = contentNode => contentNode.scrollHeight > contentNode.clientHeight;
const handleScroll = (_ref, type, showHeaderCloseButton, actions) => {
  let {
    secondaryHeaderRef,
    headerRef,
    contentRef,
    actionsRef
  } = _ref;
  const secondaryHeaderNode = secondaryHeaderRef === null || secondaryHeaderRef === void 0 ? void 0 : secondaryHeaderRef.current;
  const headerNode = headerRef === null || headerRef === void 0 ? void 0 : headerRef.current;
  const contentNode = contentRef === null || contentRef === void 0 ? void 0 : contentRef.current;
  const actionsNode = actionsRef === null || actionsRef === void 0 ? void 0 : actionsRef.current;
  if (contentNode && isContentScrollable(contentNode)) {
    const {
      scrollTop
    } = contentNode;
    if (secondaryHeaderNode && SECONDARY_HEADER_SHOW_TITLE_SCROLLS[type]) {
      const showTitleScroll = SECONDARY_HEADER_SHOW_TITLE_SCROLLS[type];
      if (showHeaderCloseButton) {
        manageClassList(scrollTop < SECONDARY_HEADER_SCROLL_SHOW ? 'remove' : 'add', secondaryHeaderNode, "".concat(namespace$5, "__header--show"));
      } else {
        manageClassList(headerNode && scrollTop < headerNode.clientHeight - showTitleScroll ? 'remove' : 'add', secondaryHeaderNode, "".concat(namespace$5, "__header--show"));
      }
      manageClassList(headerNode && scrollTop < headerNode.clientHeight - showTitleScroll ? 'remove' : 'add', secondaryHeaderNode, "".concat(namespace$5, "__header--show-title"));
    } else if (headerNode) {
      manageClassList(scrollTop < headerNode.clientHeight ? 'remove' : 'add', headerNode, "".concat(namespace$5, "__header--sticky"));
    }
    if (actions !== null && actions !== void 0 && actions.fixed && actionsNode) {
      const isScrollEnd = Math.trunc(contentNode.scrollHeight - scrollTop) <= contentNode.clientHeight;
      manageClassList(isScrollEnd ? 'remove' : 'add', actionsNode, "".concat(namespace$5, "__actions--border"));
      if (FIXED_ACTIONS_PADDINGS[type]) {
        const {
          initial,
          final
        } = FIXED_ACTIONS_PADDINGS[type];
        const scrollDiff = contentNode.scrollHeight - Math.abs(scrollTop) - contentNode.clientHeight;
        const isScrollingBack = scrollDiff > final && actionsNode.classList.contains('padding');
        if (isScrollEnd) {
          requestAnimationFrame(() => {
            actionsNode.style.paddingBottom = "".concat(final, "px");
          });
          setTimeout(() => manageClassList('add', actionsNode, 'padding'), 80);
        } else if (isScrollingBack) {
          requestAnimationFrame(() => {
            actionsNode.style.paddingBottom = "".concat(initial, "px");
          });
          setTimeout(() => manageClassList('remove', actionsNode, 'padding'), 80);
        }
      }
    }
  }
};
const namespace$4 = "".concat(namespace$5, "__actions");
const ModalActions = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    children,
    fixed = false
  } = _ref;
  const modalActionClassnames = classNames(namespace$4, {
    ["".concat(namespace$4, "--fixed")]: fixed
  });
  return /*#__PURE__*/React.createElement("div", {
    className: modalActionClassnames,
    ref: ref
  }, children);
});
var ModalActions$1 = ModalActions;
var module$2 = "@andes/modal";
var locale$2 = "en-US";
var translations$2 = {
  "andes-modal\u0004Cerrar": [null, "Close"]
};
var enTranslations = {
  module: module$2,
  locale: locale$2,
  translations: translations$2
};
var module$1 = "@andes/modal";
var locale$1 = "es-AR";
var translations$1 = {
  "andes-modal\u0004Cerrar": [null, "Cerrar"]
};
var esTranslations = {
  module: module$1,
  locale: locale$1,
  translations: translations$1
};
var module = "@andes/modal";
var locale = "pt-BR";
var translations = {
  "andes-modal\u0004Cerrar": [null, "Fechar"]
};
var ptTranslations = {
  module: module,
  locale: locale,
  translations: translations
};
const namespace$3 = "".concat(namespace$5, "__close-button");
const ModalCloseButton = _ref => {
  let {
    closeButtonSrLabel,
    onClose,
    color = 'rgba(0,0,0,0.55)',
    closeUrl = ''
  } = _ref;
  const i18n = getPatchedI18n(AndesContext, enTranslations, esTranslations, ptTranslations);
  const handleClick = event => {
    if (typeof onClose === 'function') onClose(event);
  };
  const Component = closeUrl ? 'a' : 'button';
  const handleKeyDown = event => {
    switch (event.code) {
      case ENTER:
      case SPACE:
        event.preventDefault();
        handleClick(event);
        break;
    }
  };
  return /*#__PURE__*/_jsx(Component, {
    className: namespace$3,
    "aria-label": closeButtonSrLabel || i18n.pgettext(namespace$5, 'Cerrar'),
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    href: closeUrl,
    type: closeUrl ? undefined : 'button'
  }, void 0, /*#__PURE__*/_jsx(Close20, {
    color: color
  }));
};
var ModalCloseButton$1 = ModalCloseButton;
const namespace$2 = "".concat(namespace$5, "__header");
const ModalHeader = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    children,
    onClose,
    centered = false,
    showCloseButton = true,
    closeButtonSrLabel,
    className,
    closeUrl
  } = _ref;
  const headerClassnames = classNames(namespace$2, className);
  const headerContentsClassnames = classNames("".concat(namespace$2, "__contents"), {
    ["".concat(namespace$2, "__contents--centered")]: centered
  });
  return /*#__PURE__*/React.createElement("div", {
    className: headerClassnames,
    ref: ref
  }, /*#__PURE__*/_jsx("div", {
    className: headerContentsClassnames
  }, void 0, children), showCloseButton && /*#__PURE__*/_jsx(ModalCloseButton$1, {
    onClose: onClose,
    closeButtonSrLabel: closeButtonSrLabel,
    closeUrl: closeUrl
  }));
});
var ModalHeader$1 = ModalHeader;
const namespace$1 = "".concat(namespace$5, "__title");
const ModalTitle = _ref => {
  let {
    children,
    id
  } = _ref;
  return /*#__PURE__*/_jsx("span", {
    id: "".concat(id, "-title"),
    role: "heading",
    "aria-level": 1,
    className: namespace$1
  }, void 0, children);
};
var ModalTitle$1 = ModalTitle;
const namespace = "".concat(namespace$5, "__content");
const ModalContent = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    children
  } = _ref;
  return /*#__PURE__*/React.createElement("div", {
    className: namespace,
    ref: ref
  }, children);
});
var ModalContent$1 = ModalContent;
const Modal = /*#__PURE__*/function () {
  const Modal = /*#__PURE__*/forwardRef((props, ref) => {
    var _ModalTitle, _ModalTitle2;
    const {
      'aria-label': ariaLabel,
      actions,
      children,
      className,
      closable: closableProp = true,
      closeButtonLabel: _willdeprecate$closeButtonLabel,
      closeButtonSrLabel,
      closeUrl = '',
      container,
      dialog: _willdeprecate$dialog,
      focusScope = true,
      handleClose,
      id,
      isClosing,
      isVisible,
      maxHeight,
      maxWidth,
      media,
      srLabel,
      title,
      type = 'small'
    } = props;
    const modalRef = useRef(null);
    const secondaryHeaderRef = useRef(null);
    const headerRef = useRef(null);
    const contentRef = useRef(null);
    const actionsRef = useRef(null);
    const {
      device
    } = useContext(AndesContext);
    const isWebviewIOS = isWebviewIOSOs(device);
    const closable = closableProp && !_willdeprecate$dialog;
    usePreventScroll();
    const setInitialFixedActionsShadow = useCallback(() => {
      const actionsNode = actionsRef === null || actionsRef === void 0 ? void 0 : actionsRef.current;
      if (actions !== null && actions !== void 0 && actions.fixed && actionsNode && contentRef !== null && contentRef !== void 0 && contentRef.current && isContentScrollable(contentRef === null || contentRef === void 0 ? void 0 : contentRef.current)) {
        manageClassList('add', actionsNode, "".concat(namespace$5, "__actions--border"));
      }
    }, [actions]);
    useEffect(() => {
      setTimeout(setInitialFixedActionsShadow, 0);
    }, [setInitialFixedActionsShadow]);
    const {
      overlayProps
    } = useOverlay({
      onClose: handleClose,
      isOpen: isVisible || isClosing,
      isKeyboardDismissDisabled: !closable,
      isDismissable: closable
    }, modalRef);
    const {
      dialogProps
    } = useDialog(_objectSpread(_objectSpread({}, props), {}, {
      'aria-label': srLabel || ariaLabel,
      'aria-labelledby': !(srLabel || ariaLabel) && typeof title === 'string' ? "".concat(id, "-title") : undefined
    }), modalRef);
    useModal({
      isOpen: isVisible && !isClosing,
      portalSelector: ".".concat(namespace$5, "__portal"),
      portalContainer: container
    });
    const showHeaderCloseButton = type !== 'card' && closable;
    const showOutsideCloseButton = type === 'card' && closable;
    const handleModalScroll = () => {
      handleScroll({
        secondaryHeaderRef,
        headerRef,
        contentRef,
        actionsRef
      }, type, showHeaderCloseButton, actions);
    };
    const modalClassNames = classNames(namespace$5, className, {
      ["".concat(namespace$5, "--").concat(type)]: type,
      ["".concat(namespace$5, "--asset")]: media === null || media === void 0 ? void 0 : media.asset,
      ["".concat(namespace$5, "--fixed-actions")]: actions === null || actions === void 0 ? void 0 : actions.fixed,
      ["".concat(namespace$5, "--closing")]: isClosing
    });
    const overlayClassNames = classNames("".concat(namespace$5, "__overlay"), {
      ["".concat(namespace$5, "__overlay--").concat(type)]: type,
      ["".concat(namespace$5, "__overlay--closing")]: isClosing,
      ["".concat(namespace$5, "__overlay--full-webview-ios")]: type === 'full' && isWebviewIOS
    });
    const modalStyle = {
      maxHeight: maxHeight ? "min(100%, ".concat(maxHeight, "px)") : '100%',
      maxWidth: maxWidth ? "min(100%, ".concat(maxWidth, "px)") : '100%'
    };
    const headerBaseProps = {
      onClose: handleClose,
      closeButtonSrLabel: closeButtonSrLabel || _willdeprecate$closeButtonLabel,
      closeUrl
    };
    const renderSecondaryHeader = () => {
      const asset = media === null || media === void 0 ? void 0 : media.asset;
      if (!asset) return null;
      return /*#__PURE__*/React.createElement(ModalHeader$1, _extends({
        className: "".concat(namespace$5, "__header--secondary"),
        ref: secondaryHeaderRef,
        showCloseButton: showHeaderCloseButton
      }, headerBaseProps), title && (_ModalTitle || (_ModalTitle = /*#__PURE__*/_jsx(ModalTitle$1, {
        id: id
      }, void 0, title))));
    };
    const renderHeader = () => {
      const asset = media === null || media === void 0 ? void 0 : media.asset;
      if (!(title || asset)) return null;
      return /*#__PURE__*/React.createElement(ModalHeader$1, _extends({
        ref: headerRef,
        centered: !!asset || type === 'card',
        showCloseButton: !asset && showHeaderCloseButton
      }, headerBaseProps), asset, title && (_ModalTitle2 || (_ModalTitle2 = /*#__PURE__*/_jsx(ModalTitle$1, {
        id: id
      }, void 0, title))));
    };
    return /*#__PURE__*/_jsx(Portal, {
      className: "".concat(namespace$5, "__portal"),
      container: container
    }, void 0, /*#__PURE__*/React.createElement("div", {
      className: overlayClassNames,
      ref: ref
    }, /*#__PURE__*/React.createElement(FocusScope, _extends({}, focusScope ? {
      contain: true
    } : {}, {
      restoreFocus: true,
      autoFocus: true
    }), /*#__PURE__*/React.createElement("div", _extends({}, overlayProps, dialogProps, {
      className: modalClassNames,
      style: modalStyle,
      "data-ismodal": true,
      "aria-modal": true,
      ref: modalRef
    }), /*#__PURE__*/_jsx("div", {
      tabIndex: 0,
      className: "".concat(namespace$5, "__focus-retainer")
    }), showOutsideCloseButton && /*#__PURE__*/_jsx(ModalCloseButton$1, {
      onClose: handleClose,
      closeButtonSrLabel: closeButtonSrLabel || _willdeprecate$closeButtonLabel,
      color: "white",
      closeUrl: closeUrl
    }), renderSecondaryHeader(), /*#__PURE__*/React.createElement("div", {
      className: "".concat(namespace$5, "__scroll"),
      onScroll: handleModalScroll,
      ref: contentRef
    }, renderHeader(), children && /*#__PURE__*/_jsx(ModalContent$1, {}, void 0, children), actions && /*#__PURE__*/React.createElement(ModalActions$1, {
      fixed: actions.fixed,
      ref: actionsRef
    }, actions.primary, actions.secondary))))));
  });
  Modal.displayName = 'Modal';
  return Modal;
}();
var Modal$1 = withNavigation$1(Modal);
export { Modal$1 as Modal, Modal$1 as default };
